import React, { useState, useMemo } from 'react';
import { Card, Table, Statistic, Row, Col, Empty, Skeleton, Select, Button, Dropdown, Tag } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useResponsive } from '../../../hooks/useResponsive';
import { useCurrency } from '../../../hooks/useCurrency';
import { useGetCashRegisterSalesDetailQuery } from '../../../services/reportsApi';
import { useGetCashRegisterHistoryQuery } from '../../../services/cashRegisterApi';
import { useAppSelector } from '../../../hooks/redux';
import { exportCSV, exportExcel, exportPDF, getPeriodLabel, ExportConfig } from '../../../utils/reportExport';
import { useRestaurantTimeZone } from '../../../hooks/useTimeZone';
import type { OrderPaymentDetailDto } from '../../../types/reports';

interface SalesDetailByCashRegisterProps {
  restaurantId: string;
  fromDate: string;
  toDate: string;
  selectedPeriod?: string;
  customDateRange?: [any, any] | null;
}

const PAYMENT_METHOD_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'cash', label: 'Efectivo' },
  { value: 'credit_card', label: 'Tarjeta de Crédito' },
  { value: 'debit_card', label: 'Tarjeta de Débito' },
  { value: 'transfer', label: 'Transferencia' },
];

const PAYMENT_METHOD_COLORS: Record<string, string> = {
  cash: 'green',
  credit_card: 'purple',
  debit_card: 'blue',
  transfer: 'orange',
};

// Flatten orders into one row per payment for the table
interface FlattenedPaymentRow {
  key: string;
  orderId: string;
  orderNumber: number;
  orderType: string;
  serverName: string;
  closeTime?: string;
  orderTotal: number;
  paymentMethodCode: string;
  paymentMethodName: string;
  paymentAmount: number;
  reference?: string;
}

const SalesDetailByCashRegister: React.FC<SalesDetailByCashRegisterProps> = ({
  restaurantId,
  fromDate,
  toDate,
  selectedPeriod,
  customDateRange,
}) => {
  const { isMobile } = useResponsive();
  const { currencySymbol } = useCurrency();
  const { currentRestaurant } = useAppSelector((state) => state.restaurant);
  const { formatInTimeZone } = useRestaurantTimeZone();

  const [selectedCashRegisterId, setSelectedCashRegisterId] = useState<string>('');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<string>('');

  // Get cash register history for the selector
  const { data: cashRegisters, isLoading: isLoadingRegisters } = useGetCashRegisterHistoryQuery({
    query: { fromDate, toDate, pageSize: 50 },
  });

  // Get sales detail for the selected register
  const { data, isLoading } = useGetCashRegisterSalesDetailQuery(
    {
      restaurantId,
      cashRegisterId: selectedCashRegisterId,
      paymentMethodFilter: paymentMethodFilter || undefined,
    },
    { skip: !restaurantId || !selectedCashRegisterId }
  );

  // Format register options for the selector
  const registerOptions = useMemo(() => {
    if (!cashRegisters) return [];
    return cashRegisters.map((cr) => {
      const openDate = formatInTimeZone(cr.openTime, 'DD/MM HH:mm');
      const closeDate = cr.closeTime
        ? formatInTimeZone(cr.closeTime, 'DD/MM HH:mm')
        : 'Abierta';
      const status = cr.closeTime ? '' : ' 🟢';
      return {
        value: cr.id,
        label: `${cr.userName || 'Sin nombre'} - ${openDate} → ${closeDate}${status}`,
      };
    });
  }, [cashRegisters, formatInTimeZone]);

  // Flatten orders into payment rows
  const flattenedRows = useMemo((): FlattenedPaymentRow[] => {
    if (!data?.orders) return [];
    return data.orders.flatMap((order: OrderPaymentDetailDto) =>
      order.payments.map((payment, idx) => ({
        key: `${order.orderId}-${idx}`,
        orderId: order.orderId,
        orderNumber: order.orderNumber,
        orderType: order.orderType,
        serverName: order.serverName,
        closeTime: order.closeTime,
        orderTotal: order.orderTotal,
        paymentMethodCode: payment.paymentMethodCode,
        paymentMethodName: payment.paymentMethodName,
        paymentAmount: payment.amount,
        reference: payment.reference,
      }))
    );
  }, [data]);

  // Server name filters
  const serverFilters = useMemo(() => {
    if (!flattenedRows.length) return [];
    return Array.from(new Set(flattenedRows.map((r) => r.serverName)))
      .map((s) => ({ text: s, value: s }));
  }, [flattenedRows]);

  // Payment method filters for table column
  const paymentMethodTableFilters = useMemo(() => {
    if (!flattenedRows.length) return [];
    return Array.from(new Set(flattenedRows.map((r) => r.paymentMethodName)))
      .map((m) => ({ text: m, value: m }));
  }, [flattenedRows]);

  const handleExportGrid = (format: 'csv' | 'excel' | 'pdf') => {
    if (!data || !flattenedRows.length) return;

    const periodLabel = getPeriodLabel(selectedPeriod || 'today', customDateRange);
    const config: ExportConfig = {
      reportTitle: 'Detalle de Ventas por Caja',
      periodLabel,
      restaurantName: currentRestaurant?.name || 'N/A',
      timestamp: formatInTimeZone(new Date(), 'DD/MM/YYYY HH:mm'),
      summaryCards: [
        { label: 'Total Efectivo', value: `${currencySymbol} ${data.totalCash.toFixed(2)}` },
        { label: 'Total T. Crédito', value: `${currencySymbol} ${data.totalCreditCard.toFixed(2)}` },
        { label: 'Total T. Débito', value: `${currencySymbol} ${data.totalDebitCard.toFixed(2)}` },
        { label: 'Total Transferencia', value: `${currencySymbol} ${data.totalTransfer.toFixed(2)}` },
        { label: 'Total General', value: `${currencySymbol} ${data.grandTotal.toFixed(2)}` },
        { label: 'Órdenes', value: `${data.totalOrders}` },
      ],
      sections: [{
        title: 'Detalle de Pagos por Orden',
        titleStyle: 'cash',
        headers: ['# Orden', 'Tipo', 'Mesero', 'Hora Cierre', 'Total Orden', 'Método Pago', 'Monto Pago', 'Referencia'],
        rows: flattenedRows.map((r) => [
          `${r.orderNumber}`,
          r.orderType,
          r.serverName,
          r.closeTime ? formatInTimeZone(r.closeTime, 'DD/MM HH:mm') : '-',
          `${currencySymbol} ${r.orderTotal.toFixed(2)}`,
          r.paymentMethodName,
          `${currencySymbol} ${r.paymentAmount.toFixed(2)}`,
          r.reference || '-',
        ]),
      }],
    };

    if (format === 'csv') exportCSV(config, 'ventas-por-caja');
    else if (format === 'excel') exportExcel(config, 'ventas-por-caja');
    else exportPDF(config);
  };

  const columns = [
    {
      title: '# Orden',
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      sorter: (a: FlattenedPaymentRow, b: FlattenedPaymentRow) => a.orderNumber - b.orderNumber,
      width: 90,
    },
    {
      title: 'Tipo',
      dataIndex: 'orderType',
      key: 'orderType',
      width: 90,
      responsive: ['md'] as any,
    },
    {
      title: 'Mesero',
      dataIndex: 'serverName',
      key: 'serverName',
      filters: serverFilters,
      onFilter: (value: any, record: FlattenedPaymentRow) => record.serverName === value,
      ellipsis: true,
    },
    {
      title: 'Hora Cierre',
      dataIndex: 'closeTime',
      key: 'closeTime',
      sorter: (a: FlattenedPaymentRow, b: FlattenedPaymentRow) =>
        new Date(a.closeTime || 0).getTime() - new Date(b.closeTime || 0).getTime(),
      render: (value: string | undefined) =>
        value ? formatInTimeZone(value, 'DD/MM HH:mm') : '-',
      defaultSortOrder: 'ascend' as const,
    },
    {
      title: 'Total Orden',
      dataIndex: 'orderTotal',
      key: 'orderTotal',
      render: (value: number) => `${currencySymbol} ${value.toFixed(2)}`,
      align: 'right' as const,
      responsive: ['md'] as any,
    },
    {
      title: 'Método Pago',
      dataIndex: 'paymentMethodName',
      key: 'paymentMethodName',
      filters: paymentMethodTableFilters,
      onFilter: (value: any, record: FlattenedPaymentRow) => record.paymentMethodName === value,
      render: (value: string, record: FlattenedPaymentRow) => (
        <Tag color={PAYMENT_METHOD_COLORS[record.paymentMethodCode] || 'default'}>{value}</Tag>
      ),
    },
    {
      title: 'Monto Pago',
      dataIndex: 'paymentAmount',
      key: 'paymentAmount',
      sorter: (a: FlattenedPaymentRow, b: FlattenedPaymentRow) => a.paymentAmount - b.paymentAmount,
      render: (value: number) => (
        <span style={{ fontWeight: 500 }}>{currencySymbol} {value.toFixed(2)}</span>
      ),
      align: 'right' as const,
    },
    {
      title: 'Referencia',
      dataIndex: 'reference',
      key: 'reference',
      render: (value: string | undefined) => value || '-',
      responsive: ['lg'] as any,
    },
  ];

  const exportMenuItems = [
    { key: 'csv', label: 'CSV', onClick: () => handleExportGrid('csv') },
    { key: 'excel', label: 'Excel', onClick: () => handleExportGrid('excel') },
    { key: 'pdf', label: 'PDF', onClick: () => handleExportGrid('pdf') },
  ];

  return (
    <div>
      {/* Filters */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={14}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 13 }}>Caja</label>
          <Select
            style={{ width: '100%' }}
            placeholder="Seleccionar caja..."
            options={registerOptions}
            value={selectedCashRegisterId || undefined}
            onChange={setSelectedCashRegisterId}
            loading={isLoadingRegisters}
            showSearch
            filterOption={(input, option) =>
              (option?.label as string)?.toLowerCase().includes(input.toLowerCase()) ?? false
            }
          />
        </Col>
        <Col xs={24} md={10}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 13 }}>Método de Pago</label>
          <Select
            style={{ width: '100%' }}
            options={PAYMENT_METHOD_OPTIONS}
            value={paymentMethodFilter}
            onChange={setPaymentMethodFilter}
          />
        </Col>
      </Row>

      {/* No register selected state */}
      {!selectedCashRegisterId && (
        <Card><Empty description="Selecciona una caja para ver el detalle de ventas" /></Card>
      )}

      {/* Loading state */}
      {selectedCashRegisterId && isLoading && (
        <Card><Skeleton active paragraph={{ rows: 8 }} /></Card>
      )}

      {/* Empty state */}
      {selectedCashRegisterId && !isLoading && (!data || data.totalOrders === 0) && (
        <Card><Empty description="No hay ventas registradas en esta caja" /></Card>
      )}

      {/* Data */}
      {selectedCashRegisterId && !isLoading && data && data.totalOrders > 0 && (
        <>
          {/* Summary cards */}
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={12} sm={8} md={4}>
              <Card size="small">
                <Statistic title="Efectivo" value={data.totalCash} precision={2} prefix={currencySymbol}
                  valueStyle={{ color: 'var(--color-success-text)', fontSize: isMobile ? 16 : 20 }} />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Card size="small">
                <Statistic title="T. Crédito" value={data.totalCreditCard} precision={2} prefix={currencySymbol}
                  valueStyle={{ color: '#722ed1', fontSize: isMobile ? 16 : 20 }} />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Card size="small">
                <Statistic title="T. Débito" value={data.totalDebitCard} precision={2} prefix={currencySymbol}
                  valueStyle={{ color: '#1890ff', fontSize: isMobile ? 16 : 20 }} />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Card size="small">
                <Statistic title="Transferencia" value={data.totalTransfer} precision={2} prefix={currencySymbol}
                  valueStyle={{ color: '#fa8c16', fontSize: isMobile ? 16 : 20 }} />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Card size="small">
                <Statistic title="Total General" value={data.grandTotal} precision={2} prefix={currencySymbol}
                  valueStyle={{ fontWeight: 'bold', fontSize: isMobile ? 16 : 20 }} />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Card size="small">
                <Statistic title="Órdenes" value={data.totalOrders}
                  valueStyle={{ fontSize: isMobile ? 16 : 20 }} />
              </Card>
            </Col>
          </Row>

          {/* Table */}
          <Card>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
              <Dropdown menu={{ items: exportMenuItems }}>
                <Button icon={<DownloadOutlined />} size="small">
                  Exportar
                </Button>
              </Dropdown>
            </div>
            <Table
              columns={columns}
              dataSource={flattenedRows}
              rowKey="key"
              pagination={{ defaultPageSize: 20, showSizeChanger: true }}
              scroll={isMobile ? { x: 800 } : undefined}
              size="middle"
              summary={() => (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={isMobile ? 4 : 6} align="right">
                      <strong>Total</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right">
                      <strong>{currencySymbol} {flattenedRows.reduce((sum, r) => sum + r.paymentAmount, 0).toFixed(2)}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} />
                  </Table.Summary.Row>
                </Table.Summary>
              )}
            />
          </Card>
        </>
      )}
    </div>
  );
};

export default SalesDetailByCashRegister;
